/**
 * Uputstvo za administratora platforme - šta program radi i zašto (ADR-071).
 *
 * Tekst prati stvarno ponašanje servera (affiliate-rules.ts, affiliate.service.ts);
 * kad se pravilo promeni, menja se i ovde - netačno uputstvo vodi pogrešnoj odluci
 * o novcu.
 */
import { useState, type ReactNode } from 'react';
import {
  ArrowLeft, ArrowRight, BadgeCheck, Banknote, ChevronDown, Coins, Globe, Network, QrCode, ShieldCheck, Tag, UserCheck,
} from 'lucide-react';
import { btnPrimary, btnSecondary, card, mono, napomena, warnBox } from './shared';

export type GuideTarget = 'offers' | 'networks' | 'requests' | 'conversions' | 'payouts' | 'fx' | 'settings';

type Korak = {
  naslov: string;
  kratko: string;
  ikona: ReactNode;
  sta: ReactNode;
  zasto: ReactNode;
  pazi?: ReactNode;
  gde: Array<{ label: string; target: GuideTarget }>;
};

const KORACI: Korak[] = [
  {
    naslov: 'Mreža',
    kratko: 'Veza sa affiliate mrežom',
    ikona: <Network size={18} />,
    sta: (
      <>
        <p>Napravi mrežu sa kojom Efikasa ima ugovor (npr. Travelpayouts, Impact). Ako mreža <b>javlja prodaju</b>{' '}
          (postback), posle čuvanja dobijaš <b>tajnu</b> i pun <b>postback URL</b> - unesi ga u panel mreže kao adresu za
          obaveštenja o prodaji. Partner koji ima <b>samo link</b> dodaje se sa isključenim „Mreža javlja prodaju": njegove
          ponude su „poseban link za svaki lokal", a zaradu unosiš ručno.</p>
        <p><b>Mapiranje polja</b> govori Efikasi kako mreža naziva podatke: oznaku klika (u nju mreža vraća naš
          <code style={mono}> click_id</code>), broj transakcije, stanje, iznos provizije i valutu - ispod svakog polja piše
          čemu služi. <b>Mapa stanja</b> prevodi vrednosti mreže (npr. <code style={mono}>declined</code>) u naša četiri
          stanja. Valuta je RSD, EUR ili USD. Pre čuvanja nalepi primer potvrde u <b>„Probna potvrda"</b> i vidi kako bi
          je Efikasa pročitala.</p>
      </>
    ),
    zasto: 'Bez ove veze Efikasa ne zna da je kupac nešto kupio - nema obračuna ni nagrade. Mreža javlja prodaju sama, bez ručnog unosa.',
    pazi: 'Tajna i pun postback URL prikazuju se samo jednom. Ako procure, uradi „Rotiraj tajnu" i nov URL unesi u mrežu - stari odmah prestaje da radi.',
    gde: [{ label: 'Mreže', target: 'networks' }],
  },
  {
    naslov: 'Ponuda',
    kratko: 'Šta lokali nude kupcima',
    ikona: <Tag size={18} />,
    sta: (
      <>
        <p>Ponuda je ono što kupac vidi: naslov, opis i tekst na listiću. Ponuda je jedne od dve vrste:</p>
        <p><b>Mreža javlja prodaju</b> - <b>adresa ponude</b> je tvoj affiliate link iz mreže, u kome vrednost sub-id
          parametra zameniš sa <code style={mono}>{'{click_id}'}</code>, npr.
          <code style={mono}> https://partner.rs/esim?subid={'{click_id}'}</code>. Dozvoljen je samo https.</p>
        <p><b>Poseban link za svaki lokal</b> - za partnere bez potvrda. Partner da poseban link (ili kod) za svaki
          lokal; upisuješ ga na kartici klijenta (Linkovi za ponude). Lokal vidi ponudu tek kad ima link. Kad partner
          pošalje izveštaj i uplati, na kartici klijenta „Dodaj zaradu" uneseš proviziju za taj lokal.</p>
        <p><b>Procenat za lokal</b> je deo provizije koju mreža plati Efikasi, a koji ide lokalu. <b>Procena po
          kupovini</b> je okvirna zarada lokala - vide je samo vlasnik i menadžer lokala, kao motivaciju.</p>
      </>
    ),
    zasto: 'Efikasa određuje šta se nudi i koliko deli. Lokal nikad ne vidi pravi affiliate link - samo naš kratak link, pa se ponuda može menjati ili ugasiti bez ponovnog štampanja.',
    pazi: 'Procenat se snima na svakom QR kodu u trenutku izdavanja: izmena važi samo za nove QR kodove. Ponuda se ne briše nego gasi - QR ugašene ponude kupcu kaže da ponuda više nije dostupna.',
    gde: [{ label: 'Ponude', target: 'offers' }],
  },
  {
    naslov: 'Uključivanje lokala',
    kratko: 'Zahtev i odobrenje',
    ikona: <UserCheck size={18} />,
    sta: (
      <>
        <p>Program je besplatan i podrazumevano isključen. Administrator firme u aplikaciji otvori „Partnerske
          ponude", pročita i prihvati <b>Uslove partnerskog programa</b> i klikne „Zatraži pristup". Zahtev stiže
          ovde, na karticu Zahtevi: ti ga prihvatiš (uz izbor <b>načina nagrade</b> za tu firmu: krediti ili novac)
          ili odbiješ uz razlog koji lokal vidi.</p>
        <p>Možeš i bez zahteva: Klijenti → firma → kartica „Partnerske ponude" → „Aktiviraj program". Lokal o
          svakoj odluci dobija obaveštenje.</p>
      </>
    ),
    zasto: 'Program uključuje novac i podatke o skeniranju, pa Efikasa bira ko učestvuje, a prihvatanje uslova ostaje zapisano kao dokaz.',
    pazi: 'Uslovi programa nikad ne blokiraju prijavu u aplikaciju - traže se samo pri zahtevu za pristup.',
    gde: [{ label: 'Zahtevi', target: 'requests' }],
  },
  {
    naslov: 'QR kod u lokalu',
    kratko: 'Kasir pokazuje ponudu',
    ikona: <QrCode size={18} />,
    sta: (
      <>
        <p>Kasir ili konobar u meniju „Partnerske ponude" (web ili Android kasa: Više → Partnerske ponude) izabere
          ponudu i klikne <b>„Prikaži QR na ekranu"</b> ili <b>„Odštampaj listić"</b>. Listić je nefiskalni, sa
          oznakom „OVO NIJE FISKALNI RAČUN".</p>
        <p>Svaki prikaz dobija svoj nasumičan kod. Kupac skenira kratak link <code style={mono}>/r/…</code>, Efikasa
          zabeleži sken i preusmeri ga na sajt partnera, uz oznaku klika. Isti telefon istog dana broji se kao jedan
          sken, a botovi i pregledi linka se ne broje.</p>
      </>
    ),
    zasto: 'Kasir preporučuje ponudu kupcu kome stvarno treba - to prodaje mnogo bolje od reklame koju niko ne čita. Kod po prikazu pokazuje koji objekat i operater donose prodaju.',
    pazi: 'Operater sa dodeljenim objektima izdaje QR samo za te objekte. Iznose zarade vide samo vlasnik i menadžer, kasir ih ne vidi. Program radi i kad lokalu istekne licenca, da može da zaradi za novu; suspendovan nalog ne radi ništa.',
    gde: [],
  },
  {
    naslov: 'Mreža javlja prodaju',
    kratko: 'Prodaje i kurs',
    ikona: <Globe size={18} />,
    sta: (
      <>
        <p>Kad kupac na sajtu partnera uradi ono što ponuda traži, mreža pošalje postback. Prodaja obično ide redom
          <b> na čekanju → odobrena → naplaćeno od mreže</b>, ili završi kao <b>poništena</b> (povraćaj, otkaz,
          prevara). Isti postback poslat dvaput upisuje se jednom.</p>
        <p>Provizija u EUR ili USD preračunava se u dinare po srednjem kursu NBS na dan prodaje - server ga preuzima
          sam, na 6 sati. Ako kursa ipak nema ni za poslednjih 7 dana, prodaja je označena <b>„treba kurs"</b> i nikome
          se ne pripisuje dok ga ne uneseš na kartici Kursevi. Prodaja u drugoj valuti se upisuje kao
          <b> „nepodržana valuta"</b> i ne pripisuje se.</p>
      </>
    ),
    zasto: 'Zarada nastaje samo iz potvrđene kupovine, nikad iz samog skena - lokal ne može da zaradi tako što sam skenira svoje QR kodove.',
    pazi: '„Nesparena" prodaja znači da je mreža javila klik koji ne poznajemo (pogrešno mapiranje ili tuđ link). Vidi se na kartici Prodaje, ali se ne pripisuje nikome. Kurs se ne prepisuje - iz njega je već izračunata zarada.',
    gde: [{ label: 'Prodaje', target: 'conversions' }, { label: 'Kursevi', target: 'fx' }],
  },
  {
    naslov: 'Nagrada lokalu',
    kratko: 'Kredit ili novac',
    ikona: <Coins size={18} />,
    sta: (
      <>
        <p>Način nagrade se bira <b>za svaku firmu posebno</b> - pri prihvatanju zahteva, a kasnije na kartici
          klijenta. Globalnog podešavanja nema.</p>
        <p><b>Krediti</b>: odobrena prodaja odmah ulazi u kredit lokala. Na sledećem računu za
          pretplatu kredit se iskazuje kao stavka <b>„Popust - prebijanje provizije iz partnerskog programa"</b>.
          Račun nikad ne pada ispod najmanje naplate; ostatak kredita prelazi u sledeći period.</p>
        <p><b>Novac</b>: lokal može da podigne samo svoj deo provizije za prodaje koje je mreža Efikasi
          <b> stvarno isplatila</b>. Kad uplata od mreže legne, na kartici Prodaje označi te prodaje kao „naplaćeno od
          mreže" (ili to mreža javi sama). Lokal zatim traži isplatu iznad praga; ti je prihvatiš, uplatiš i označiš
          kao plaćenu sa referencom uplate. Ako si lokalu već isplatio novac van sistema, na kartici klijenta
          „Upiši isplatu" (ne preko raspoloživog).</p>
      </>
    ),
    zasto: 'Pravilo „samo iz novca koji je stigao" znači da Efikasa nikad ne isplaćuje iz svog džepa: ako mreža ne plati ili poništi prodaju, isplate nema. Krediti su jednostavniji - lokal ne izdaje račun, a Efikasa samo daje popust.',
    pazi: 'Način nagrade biraš po firmi (pri prihvatanju ili na kartici klijenta). Paušalac uvek dobija kredite - server odbija novac. Promena načina ne dira već odobrene prodaje. Prag isplate je u Podešavanjima.',
    gde: [{ label: 'Zahtevi', target: 'requests' }, { label: 'Isplate', target: 'payouts' }, { label: 'Podešavanja', target: 'settings' }],
  },
];

const CESTO: Array<{ p: string; o: ReactNode }> = [
  {
    p: 'Prodaja je poništena nakon što je lokal već dobio kredit?',
    o: 'Kredit lokala ide u minus za taj iznos i prebija se sa budućom zaradom. Lokalu se zbog toga nikad ništa ne naplaćuje - minus se samo prenosi.',
  },
  {
    p: 'Lokal sam skenira svoje QR kodove?',
    o: 'Sken ne donosi novac - zarada postoji samo kad mreža potvrdi kupovinu. Ako primetiš zloupotrebu (npr. lažne kupovine), isključi program na kartici klijenta; mreža takve prodaje obično poništi.',
  },
  {
    p: 'Šta se desi kad isključim program lokalu?',
    o: 'Već odštampani i prikazani QR kodovi tog lokala prestaju da preusmeravaju (kupac vidi „Ponuda više nije dostupna"), a novi se ne mogu izdati. Zarađen kredit ostaje i troši se na sledećim računima za pretplatu.',
  },
  {
    p: 'Promenio sam procenat za lokal - važi li za stare QR kodove?',
    o: 'Ne. Procenat je snimljen na svakom QR kodu kad je izdat. Novi procenat važi samo za QR kodove izdate posle izmene.',
  },
  {
    p: 'Kada koristim ručnu korekciju?',
    o: 'Kad treba ispraviti kredit lokala mimo prodaja (dogovor, greška u obračunu, nagrada). Iznos sa predznakom (+ ili −) i obavezan razlog; korekcija se samo dopisuje u knjigu nagrada i vidi se zauvek.',
  },
  {
    p: 'Partner nema postback - samo link. Kako lokal zarađuje?',
    o: 'Mrežu dodaj sa isključenim „Mreža javlja prodaju", a ponudu kao „Poseban link za svaki lokal". Od partnera traži poseban link (ili kod) za svaki lokal i upiši ga na kartici klijenta. Kad partner pošalje izveštaj po linkovima i uplati, na kartici klijenta „Dodaj zaradu": provizija za taj lokal, datum i referenca uplate. Zajednički link za sve lokale se namerno ne podržava - ne bi se znalo koji lokal je koliko zaradio.',
  },
  {
    p: 'Pogrešno sam uneo ručnu zaradu?',
    o: 'U tabeli prodaja na kartici klijenta, kod te zarade „Poništi" uz razlog. Upis ostaje u istoriji kao poništen; ako je kredit već iskorišćen ili novac isplaćen, stanje lokala ide u minus i prebija se sa budućom zaradom.',
  },
  {
    p: 'Ko u panelu sme šta?',
    o: 'Menja samo administrator platforme. Podrška i „samo čitanje" vide sve, bez dugmadi za izmenu. Prodavci (reselleri) ovu stranicu ne vide; na kartici svog klijenta vide stanje programa.',
  },
  {
    p: 'Da li je listić fiskalni račun?',
    o: 'Nije. To je reklamni listić, sa oznakom „OVO NIJE FISKALNI RAČUN", i ne utiče na obavezu lokala da izda fiskalni račun za svoj promet.',
  },
];

const PRVO_PODESAVANJE: Array<{ t: string; target?: GuideTarget }> = [
  { t: 'Proveri prag isplate u novcu i zapiši postback osnovu.', target: 'settings' },
  { t: 'Napravi mrežu, proveri mapiranje „Probnom potvrdom", sačuvaj tajnu i postback URL unesi u panel mreže.', target: 'networks' },
  { t: 'Napravi ponudu sa affiliate linkom u kome je {click_id} i postavi procenat za lokal.', target: 'offers' },
  { t: 'Proveri da je kurs NBS za valutu mreže preuzet (izvor „NBS").', target: 'fx' },
  { t: 'Uključi jedan probni lokal (izaberi krediti ili novac), prikaži QR i skeniraj ga telefonom.', target: 'requests' },
  { t: 'Napravi probnu kupovinu (ili probni postback iz mreže) i proveri da se pojavila na kartici Prodaje kao sparena.', target: 'conversions' },
];

export function AffiliateGuide({ onIdi }: { onIdi: (target: GuideTarget) => void }) {
  const [aktivan, setAktivan] = useState(0);
  const k = KORACI[aktivan];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <style>{'.affiliate-guide-odeljak p { margin: 0 0 8px; } .affiliate-guide-odeljak p:last-child { margin-bottom: 0; }'}</style>
      <section style={card}>
        <h2 style={naslov2}>Čemu služi</h2>
        <p style={tekst}>
          Partnerske ponude su program u kome lokali koji koriste Efikasu svojim kupcima preporučuju proizvode
          partnera (npr. eSIM za putovanja). Kupac skenira QR kod sa ekrana kase ili sa listića i kupuje na sajtu
          partnera. Partner kroz affiliate mrežu plaća proviziju Efikasi, a Efikasa deo te provizije daje lokalu -
          kao popust na pretplatu ili u novcu.
        </p>
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', marginTop: 12 }}>
          <Korist ikona={<BadgeCheck size={18} />} naslov="Za lokal">Zarada bez ulaganja: kasir pokaže QR, a kredit umanjuje račun za Efikasu.</Korist>
          <Korist ikona={<Banknote size={18} />} naslov="Za Efikasu">Prihod od provizije i razlog više da lokal ostane na Efikasi.</Korist>
          <Korist ikona={<ShieldCheck size={18} />} naslov="Bez rizika">Nagrada samo iz potvrđene kupovine; novac samo iz provizije koja je stvarno stigla.</Korist>
        </div>
      </section>

      <section style={card}>
        <h2 style={naslov2}>Kako radi - korak po korak</h2>
        <ol style={{ listStyle: 'none', padding: 0, margin: '12px 0 16px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {KORACI.map((s, i) => {
            const izabran = i === aktivan;
            const prosao = i < aktivan;
            return (
              <li key={s.naslov} style={{ flex: '1 1 140px' }}>
                <button
                  onClick={() => setAktivan(i)}
                  data-testid={`affiliate-guide-step-${i + 1}`}
                  aria-current={izabran ? 'step' : undefined}
                  style={{
                    width: '100%', textAlign: 'left', cursor: 'pointer', padding: '10px 12px', borderRadius: 8,
                    border: `1px solid ${izabran ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    background: izabran ? 'rgba(37,99,235,0.12)' : 'var(--color-bg)',
                    color: 'var(--color-text)', display: 'flex', gap: 10, alignItems: 'center',
                  }}
                >
                  <span style={{
                    flexShrink: 0, width: 26, height: 26, borderRadius: 999, display: 'inline-flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 12, fontWeight: 700,
                    background: izabran || prosao ? 'var(--color-primary)' : 'transparent',
                    color: izabran || prosao ? '#fff' : 'var(--color-text-muted)',
                    border: izabran || prosao ? 'none' : '1px solid var(--color-border)',
                  }}>{i + 1}</span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--color-text-strong)' }}>{s.naslov}</span>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--color-text-muted)' }}>{s.kratko}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>

        <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-strong)', marginBottom: 8 }}>
            {k.ikona}
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Korak {aktivan + 1}: {k.naslov}</h3>
          </div>
          <Odeljak naslov="Šta se radi">{k.sta}</Odeljak>
          <Odeljak naslov="Zašto">{<p>{k.zasto}</p>}</Odeljak>
          {k.pazi && <div style={{ ...warnBox, marginBottom: 12 }}><span><b>Pazi:</b> {k.pazi}</span></div>}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {k.gde.map((g) => (
              <button key={g.target} onClick={() => onIdi(g.target)} style={btnPrimary}>
                Otvori „{g.label}" <ArrowRight size={16} />
              </button>
            ))}
            {k.gde.length === 0 && <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Ovaj korak se dešava u aplikaciji lokala, ne u panelu.</span>}
            <div style={{ flex: 1 }} />
            <button onClick={() => setAktivan(aktivan - 1)} disabled={aktivan === 0} style={{ ...btnSecondary, opacity: aktivan === 0 ? 0.5 : 1 }}>
              <ArrowLeft size={16} /> Prethodni
            </button>
            <button onClick={() => setAktivan(aktivan + 1)} disabled={aktivan === KORACI.length - 1} style={{ ...btnSecondary, opacity: aktivan === KORACI.length - 1 ? 0.5 : 1 }}>
              Sledeći <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      <section style={card}>
        <h2 style={naslov2}>Primer obračuna</h2>
        <p style={tekst}>Kupac kupi eSIM preko QR koda. Mreža javi proviziju od 10 EUR, kurs je 117,20 RSD, a procenat za lokal na tom QR kodu je 40%.</p>
        <table style={{ borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
          <tbody>
            <Red l="Provizija koju mreža plaća Efikasi" v="10,00 EUR × 117,20 = 1.172,00 RSD" />
            <Red l="Lokalu (40%)" v="468,80 RSD" jak />
            <Red l="Ostaje Efikasi (60%)" v="703,20 RSD" />
          </tbody>
        </table>
        <div style={{ ...napomena, marginTop: 12 }}>
          <b>Krediti:</b> 468,80 RSD je odmah u kreditu čim mreža odobri prodaju i umanjuje sledeći račun za pretplatu.
          <br />
          <b>Novac:</b> 468,80 RSD postaje raspoloživo za isplatu tek kad prodaju označiš „naplaćeno od mreže", a lokal
          može da zatraži isplatu kad zbir pređe prag.
        </div>
      </section>

      <section style={card}>
        <h2 style={naslov2}>Prvo podešavanje - kontrolna lista</h2>
        <ol style={{ margin: '10px 0 0', paddingLeft: 20, display: 'grid', gap: 8, fontSize: 13, color: 'var(--color-text)' }}>
          {PRVO_PODESAVANJE.map((s) => (
            <li key={s.t}>
              {s.t}{' '}
              {s.target && (
                <button onClick={() => onIdi(s.target!)} style={linkDugme}>otvori</button>
              )}
            </li>
          ))}
        </ol>
      </section>

      <section style={card}>
        <h2 style={naslov2}>Česte situacije</h2>
        <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
          {CESTO.map((c) => (
            <details key={c.p} style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: '10px 12px', background: 'var(--color-bg)' }}>
              <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--color-text-strong)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <ChevronDown size={14} /> {c.p}
              </summary>
              <p style={{ ...tekst, margin: '8px 0 0' }}>{c.o}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}

function Korist({ ikona, naslov, children }: { ikona: ReactNode; naslov: string; children: ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 12, background: 'var(--color-bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13, color: 'var(--color-text-strong)' }}>{ikona} {naslov}</div>
      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4, lineHeight: 1.5 }}>{children}</div>
    </div>
  );
}

function Odeljak({ naslov, children }: { naslov: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', fontWeight: 600, marginBottom: 4 }}>{naslov}</div>
      <div style={{ ...tekst, margin: 0 }} className="affiliate-guide-odeljak">{children}</div>
    </div>
  );
}

function Red({ l, v, jak }: { l: string; v: string; jak?: boolean }) {
  return (
    <tr>
      <td style={{ padding: '4px 16px 4px 0', color: 'var(--color-text-muted)' }}>{l}</td>
      <td style={{ padding: '4px 0', color: 'var(--color-text-strong)', fontWeight: jak ? 700 : 500 }}>{v}</td>
    </tr>
  );
}

const naslov2: React.CSSProperties = { margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--color-text-strong)' };
const tekst: React.CSSProperties = { margin: '8px 0 0', fontSize: 13, lineHeight: 1.6, color: 'var(--color-text)' };
const linkDugme: React.CSSProperties = { background: 'none', border: 'none', padding: 0, color: 'var(--color-primary)', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' };
